import { useEffect, useRef, useState } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import Calendar from './pages/Calendar'
import Home from './pages/Home'
import NotFound from './pages/NotFound'
import Login from './pages/Auth/Login'
import Register from './pages/Auth/Register'
import ForgotPassword from './pages/Auth/ForgotPassword'
import UpdatePassword from './pages/Auth/UpdatePassword'
import { FamilySettings } from './pages/FamilySettings'
import { AcceptInvitation } from './pages/AcceptInvitation'
import PrivacyPolicy from './pages/PrivacyPolicy'
import Terms from './pages/Terms'
import Account from './pages/Account'
import Pricing from './pages/Pricing'
import BlogIndex from './pages/BlogIndex'
import BlogPostPage from './pages/BlogPostPage'
import Contact from './pages/Contact'
import { useAuthStore } from './stores/authStore'
import { useFamilyStore } from './stores/familyStore'
import { useUserPreferencesStore } from './stores/userPreferencesStore'
import { ClerkAuthSync } from './components/auth/ClerkAuthSync'
import { FamilyDataSync } from './components/auth/FamilyDataSync'
import { EncryptionProvider } from './contexts/EncryptionContext'
import { DeviceSyncBanner } from './components/auth/DeviceSyncBanner'
import logger from './utils/logger'
import { track } from './utils/analytics'
import { DebugConsole } from './components/debug/DebugConsole'

// Komponent ładowania
const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
      <p className="text-slate-600">Loading...</p>
    </div>
  </div>
)

// Komponent dla chronionych tras (wymaga zalogowania)
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, initialized } = useAuthStore()
  const { initialized: familyInitialized, isLoading: familyLoading } = useFamilyStore()
  const navigate = useNavigate()

  useEffect(() => {
    if (initialized && !loading && !user) {
      navigate('/auth/login')
    }
  }, [user, loading, initialized, navigate])

  if (!initialized || loading) {
    return <LoadingScreen />
  }

  if (!user) {
    return <Navigate to="/auth/login" />
  }

  // FamilyDataSync handles loading family data via Convex reactive queries
  if (!familyInitialized || familyLoading) {
    return <LoadingScreen />
  }

  return (
    <>
      <DeviceSyncBanner />
      {children}
    </>
  )
}

// Komponent dla strony głównej - przekierowuje zalogowanych do kalendarza
const HomeRoute = () => {
  const { user, loading, initialized } = useAuthStore()
  const { initialized: familyInitialized, isLoading: familyLoading } = useFamilyStore()

  if (!initialized || loading) {
    return <LoadingScreen />
  }

  if (user) {
    // FamilyDataSync handles loading family data via Convex reactive queries
    if (!familyInitialized || familyLoading) {
      return <LoadingScreen />
    }
    return <Navigate to="/calendar" replace />
  }

  return <Home />
}

// Komponent dla tras publicznych (dostępne dla niezalogowanych)
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, initialized } = useAuthStore()

  if (!initialized || loading) {
    return <LoadingScreen />
  }

  return !user ? <>{children}</> : <Navigate to="/calendar" />
}

export default function App() {
  const { initialized, loading, user } = useAuthStore()
  const { loadPreferences } = useUserPreferencesStore()
  const [loadError, setLoadError] = useState(false)

  // Synchronizacja stanu Clerk → Zustand authStore (musi być wyrenderowane zawsze!)
  return (
    <>
      <ClerkAuthSync />
      <EncryptionProvider>
        <AppContent
          initialized={initialized}
          loading={loading}
          user={user}
          loadPreferences={loadPreferences}
          loadError={loadError}
          setLoadError={setLoadError}
        />
      </EncryptionProvider>
    </>
  )
}

function AppContent({ initialized, loading, user, loadPreferences, loadError, setLoadError }: any) {

  // Załaduj preferencje użytkownika gdy zmieni się status auth
  useEffect(() => {
    if (initialized && !loading && user) {
      loadPreferences()
    }
  }, [initialized, loading, user, loadPreferences])

  // Watchdog przeciw "czarnemu ekranowi"
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return
    }

    let timeoutId: number | null = null

    const scheduleReloadIfStuck = () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId)
        timeoutId = null
      }

      if (!initialized || loading) {
        timeoutId = window.setTimeout(() => {
          if (!initialized || loading) {
            logger.error('App stuck in loading state > 10s. Halting loop for debugging.')
            setLoadError(true)
          }
        }, 10000)
      }
    }

    scheduleReloadIfStuck()

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        scheduleReloadIfStuck()
      } else if (timeoutId !== null) {
        window.clearTimeout(timeoutId)
        timeoutId = null
      }
    }

    const handleFocus = () => {
      scheduleReloadIfStuck()
    }

    window.addEventListener('focus', handleFocus)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId)
      }
      window.removeEventListener('focus', handleFocus)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [initialized, loading])

  if (loadError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 text-center bg-red-50 font-sans">
        <div className="bg-white p-8 rounded-xl shadow-xl max-w-2xl w-full border border-red-100">
          <h1 className="text-3xl font-bold text-red-600 mb-4">Critical Loading Error</h1>
          <p className="mb-6 text-slate-600 text-lg">
            The application failed to initialize authentication. This is usually caused by:
          </p>
          <ul className="text-left list-disc pl-8 mb-8 text-slate-700 space-y-2">
            <li>Strict <strong>Browser Extensions</strong> (e.g., MetaMask, Rabby) injecting "lockdown" scripts.</li>
            <li><strong>Ad Blockers</strong> blocking Clerk or Convex.</li>
            <li>A stale <strong>Service Worker</strong> caching old files.</li>
          </ul>

          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 text-left mb-8 overflow-auto">
            <p className="font-mono text-xs text-slate-500 mb-2 uppercase tracking-wide font-bold">Debug Status:</p>
            <div className="grid grid-cols-2 gap-2 font-mono text-sm text-slate-700">
              <div>Auth Initialized: <span className={initialized ? "text-green-600" : "text-red-500"}>{initialized ? 'Yes' : 'No'}</span></div>
              <div>Auth Loading: <span className={loading ? "text-amber-600" : "text-green-600"}>{loading ? 'Yes' : 'No'}</span></div>
              <div>User Authenticated: <span className={user ? "text-green-600" : "text-slate-500"}>{user ? 'Yes' : 'No'}</span></div>
            </div>
          </div>
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
            >
              Reload Application
            </button>
          </div>
          <p className="mt-6 text-xs text-slate-400">
            Please check the Developer Console (F12) for "SES Removing unpermitted intrinsics" errors.
          </p>
        </div>
      </div>
    )
  }

  if (!initialized || loading) {
    return <LoadingScreen />
  }

  return (
    <Router>
      {/* Synchronizacja stanu Clerk → Zustand authStore */}
      {/* Moved to top level */}

      {/* Synchronizacja danych rodziny Convex → Zustand familyStore */}
      <FamilyDataSync />
      <DebugConsole />
      <RouteChangeTracker />
      <Routes>
        {/* Strona główna */}
        <Route path="/" element={<HomeRoute />} />

        {/* Strony autoryzacji */}
        <Route path="/auth/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/auth/register" element={<PublicRoute><Register /></PublicRoute>} />
        <Route path="/auth/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
        <Route path="/auth/update-password" element={<UpdatePassword />} />

        {/* Onboarding (zachowany dla kompatybilności – przekierowanie do ustawień) */}
        <Route path="/onboarding" element={<ProtectedRoute><FamilySettings /></ProtectedRoute>} />

        {/* Kalendarz - wymaga zalogowania */}
        <Route path="/calendar" element={<ProtectedRoute><Calendar /></ProtectedRoute>} />

        {/* Zarządzanie rodziną */}
        <Route path="/family-settings" element={<ProtectedRoute><FamilySettings /></ProtectedRoute>} />
        {/* Konto */}
        <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />

        {/* Zaproszenia */}
        <Route path="/invitation/:token" element={<AcceptInvitation />} />

        {/* Privacy Policy – available publicly and for logged-in users */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/polityka-prywatnosci" element={<PrivacyPolicy />} />

        {/* Regulamin – dostępny publicznie i dla zalogowanych */}
        <Route path="/terms" element={<Terms />} />

        {/* Cennik – dostępny publicznie i dla zalogowanych */}
        <Route path="/pricing" element={<Pricing />} />

        {/* Blog – publiczny */}
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />

        {/* Kontakt – dostępny publicznie */}
        <Route path="/contact" element={<Contact />} />

        {/* Strona 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  )
}

function RouteChangeTracker() {
  const location = useLocation()
  const prevPathRef = useRef<string | null>(null)

  useEffect(() => {
    try {
      const w = window as any
      if (!w.__pvSent) {
        const initialPath = window.location.pathname
        const maskedInitial = initialPath.startsWith('/invitation/') ? '/invitation/[token]' : initialPath
        track('page_view', { page_path: maskedInitial })
        w.__pvSent = true
        prevPathRef.current = initialPath
      }
    } catch { }
  }, [])

  useEffect(() => {
    const path = location.pathname || window.location.pathname
    if (prevPathRef.current === path) return
    prevPathRef.current = path
    const masked = path.startsWith('/invitation/') ? '/invitation/[token]' : path
    track('page_view', { page_path: masked })
  }, [location.pathname])

  return null
}
